/**
 * Budget Overview Widget Component
 *
 * Displays top 5 projects by budget usage percentage with color-coded status.
 * Auto-refreshes every 5 minutes to keep data current.
 *
 * @module components/dashboard/BudgetOverviewWidget
 */

import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, ProgressBar, Badge, Spinner, Alert } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { formatCurrency, formatPercentage } from '../../utils/formatters';
import { BUDGET_TRACKED_PROJECT_STATUSES, excludePersonalProjects } from '../../utils/constants';
import { getBudgetColorVariant, getBudgetStatusLabel } from '../../utils/budgetStatus';
import { DataTable } from '../common/DataTable';

/**
 * BudgetOverviewWidget Component
 *
 * @param {Object} props
 * @param {number} props.autoRefreshInterval - Auto-refresh interval in milliseconds (default: 300000 = 5 minutes)
 * @param {number} props.limit - Maximum number of projects to display (default: 5)
 * @param {string} props.className - Additional CSS classes
 */
const BudgetOverviewWidget = ({
  autoRefreshInterval = 300000, // 5 minutes
  limit = 5,
  className = '',
}) => {
  const navigate = useNavigate();

  // Fetch active and quoted projects with expenses
  const { data: projectsData, isLoading, error, refetch } = useQuery({
    queryKey: ['budget-overview-widget'],
    queryFn: async () => {
      const { supabase } = await import('../../services/supabase');
      // #405: exclude per-user personal-tasks containers from the budget widget.
      const { data: projects, error } = await excludePersonalProjects(
        supabase
          .from('projects')
          .select(`
            *,
            client:clients!projects_client_id_fkey(id, company_name)
          `)
          .in('status', BUDGET_TRACKED_PROJECT_STATUSES)
      )
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      // Fetch expense + committed-PO totals for every project on this page
      // in two queries total (not per-project), and run the same usage
      // %/status math used by the project detail budget card and the
      // Historial de Proyectos table, so this widget can never drift from
      // those or the MCP agent's check_budget_status (#276, #325, #406).
      const { getBudgetTotalsByProject, calculateBudgetMetrics } = await import('../../services/projectService');
      const totalsByProject = await getBudgetTotalsByProject(projects.map((p) => p.id));

      const projectsWithBudget = projects.map((project) => {
        const { totalExpenses, committedAmount } = totalsByProject[project.id] || {
          totalExpenses: 0,
          committedAmount: 0,
        };
        // #679: `null` no es 0. Un proyecto sin cotizar mostraba "Cotizado:
        // ₡0,00" y "Uso: 0,0% — Normal" en verde; ahora el monto sale "-" y el
        // estado "Sin presupuesto" (gris), que es lo que realmente se sabe.
        const quotedAmount = project.quoted_amount ?? null;
        const { percentageUsed } = calculateBudgetMetrics(quotedAmount, totalExpenses, committedAmount);

        return {
          ...project,
          quotedAmount,
          totalExpenses,
          committedAmount,
          percentageUsed,
        };
      });

      // Sort by percentage used (descending) and take top N
      return projectsWithBudget
        .sort((a, b) => b.percentageUsed - a.percentageUsed)
        .slice(0, limit);
    },
    refetchInterval: autoRefreshInterval,
    staleTime: autoRefreshInterval - 1000, // Slightly less than refetch interval
  });

  // Manual refresh on mount (optional)
  useEffect(() => {
    const timer = setTimeout(() => {
      refetch();
    }, autoRefreshInterval);

    return () => clearTimeout(timer);
  }, [autoRefreshInterval, refetch]);

  /**
   * Handle project row click
   */
  const handleProjectClick = (projectId) => {
    navigate(`/proyectos/${projectId}`);
  };

  const budgetColumns = useMemo(() => [
    {
      id: 'project_number',
      header: 'Proyecto',
      accessor: 'project_number',
      minWidth: 120,
      cell: (p) => <code className="text-muted" style={{ fontSize: '0.85rem' }}>{p.project_number}</code>,
    },
    {
      id: 'title',
      header: 'Nombre',
      accessor: 'title',
      minWidth: 200,
      cell: (p) => (
        <div className="text-truncate fw-semibold" style={{ maxWidth: '200px' }} title={p.title}>
          {p.title}
        </div>
      ),
    },
    {
      id: 'quoted',
      header: 'Cotizado',
      accessor: (p) => p.quotedAmount || 0,
      align: 'right',
      minWidth: 120,
      cell: (p) => (
        <span className="text-muted" style={{ fontSize: '0.9rem' }}>
          {formatCurrency(p.quotedAmount)}
        </span>
      ),
    },
    {
      id: 'spent',
      header: 'Gastado',
      accessor: (p) => p.totalExpenses || 0,
      align: 'right',
      minWidth: 120,
      cell: (p) => {
        const variant = getBudgetColorVariant(p.percentageUsed);
        return (
          <span className={`text-${variant} fw-semibold`} style={{ fontSize: '0.9rem' }}>
            {formatCurrency(p.totalExpenses)}
          </span>
        );
      },
    },
    {
      id: 'usage',
      header: 'Uso',
      accessor: (p) => p.percentageUsed || 0,
      minWidth: 160,
      cell: (p) => {
        const variant = getBudgetColorVariant(p.percentageUsed);
        return (
          <>
            <div className="mb-2">
              <span
                className={`fw-bold text-${variant}`}
                style={{ fontSize: '0.95rem' }}
                data-testid={`budget-widget-percentage-${p.id}`}
              >
                {formatPercentage(p.percentageUsed, 1)}
              </span>
            </div>
            <ProgressBar
              now={Math.min(p.percentageUsed, 100)}
              variant={variant}
              style={{ height: '8px', borderRadius: '4px' }}
            />
          </>
        );
      },
    },
    {
      id: 'status',
      header: 'Estado',
      accessor: (p) => p.percentageUsed || 0,
      align: 'center',
      minWidth: 110,
      cell: (p) => {
        const variant = getBudgetColorVariant(p.percentageUsed);
        const statusLabel = getBudgetStatusLabel(p.percentageUsed);
        return (
          <Badge
            bg={variant}
            className={`px-3 py-2 ${variant === 'warning' ? 'text-dark' : ''}`}
            style={{ fontSize: '0.8rem', fontWeight: '600' }}
          >
            {statusLabel}
          </Badge>
        );
      },
    },
  ], []);

  if (isLoading) {
    return (
      <Card className={className} data-testid="budget-widget-container">
        <Card.Header className="bg-light">
          <h5 className="mb-0">
            <i className="bi bi-piggy-bank me-2"></i>
            Resumen de Presupuestos
          </h5>
        </Card.Header>
        <Card.Body className="text-center py-5">
          <Spinner animation="border" role="status" size="sm">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
          <p className="text-muted mt-2 mb-0">Cargando datos de presupuesto...</p>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className} data-testid="budget-widget-container">
        <Card.Header className="bg-light">
          <h5 className="mb-0">
            <i className="bi bi-piggy-bank me-2"></i>
            Resumen de Presupuestos
          </h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="danger" className="mb-0">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Error al cargar datos de presupuesto
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  const projects = projectsData || [];

  return (
    <Card className={`${className} shadow-sm hover-lift`} data-testid="budget-widget-container">
      <Card.Header className="bg-gradient-light">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0 fw-semibold">
            <i className="bi bi-piggy-bank me-2 text-success"></i>
            Resumen de Presupuestos
          </h5>
          <Badge bg="secondary" className="px-3 py-2">
            Top {limit} Proyectos
          </Badge>
        </div>
      </Card.Header>

      {projects.length === 0 ? (
        <Card.Body>
          <Alert variant="info" className="mb-0 border-0">
            <i className="bi bi-info-circle me-2"></i>
            No hay proyectos activos o cotizados con presupuesto asignado
          </Alert>
        </Card.Body>
      ) : (
        <>
          {/* Desktop Table View - hidden on mobile */}
          <Card.Body className="p-0 d-none d-md-block">
            <DataTable
              tableId="dashboard-budget"
              data={projects}
              rowKey={(p) => p.id}
              onRowClick={(p) => handleProjectClick(p.id)}
              columns={budgetColumns}
            />
          </Card.Body>

          {/* Mobile Cards View - visible only on mobile */}
          <Card.Body className="d-block d-md-none p-3">
            <div className="budget-cards-mobile">
              {projects.map((project) => {
                const variant = getBudgetColorVariant(project.percentageUsed);
                const statusLabel = getBudgetStatusLabel(project.percentageUsed);
                // #685: sin monto cotizado NO hay "restante". Esto era
                // `quotedAmount - totalExpenses` a secas, y con `quotedAmount`
                // en null daba el gasto en negativo pintado de rojo — el mismo
                // `remaining: -30000` que reportaba el issue, en la tarjeta.
                const remaining = project.quotedAmount == null
                  ? null
                  : project.quotedAmount - project.totalExpenses;

                return (
                  <Card
                    key={project.id}
                    className={`mb-3 budget-card-mobile border-${variant}`}
                    onClick={() => handleProjectClick(project.id)}
                    style={{ cursor: 'pointer', borderLeftWidth: '4px' }}
                    data-testid={`budget-widget-project-mobile-${project.id}`}
                  >
                    <Card.Body className="p-3">
                      {/* Header: Project name and status */}
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="flex-grow-1 me-2">
                          <code className="text-muted d-block mb-1" style={{ fontSize: '0.75rem' }}>
                            {project.project_number}
                          </code>
                          <h6 className="mb-0 fw-semibold" style={{ fontSize: '0.95rem' }}>
                            {project.title}
                          </h6>
                        </div>
                        <Badge
                          bg={variant}
                          className={`px-2 py-1 ${variant === 'warning' ? 'text-dark' : ''}`}
                          style={{ fontSize: '0.7rem', fontWeight: '600' }}
                        >
                          {statusLabel}
                        </Badge>
                      </div>

                      {/* Progress bar with percentage */}
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="text-muted" style={{ fontSize: '0.75rem' }}>Uso del presupuesto</span>
                          <span
                            className={`fw-bold text-${variant}`}
                            style={{ fontSize: '0.85rem' }}
                            data-testid={`budget-widget-percentage-mobile-${project.id}`}
                          >
                            {formatPercentage(project.percentageUsed, 1)}
                          </span>
                        </div>
                        <ProgressBar
                          now={Math.min(project.percentageUsed, 100)}
                          variant={variant}
                          style={{ height: '8px', borderRadius: '4px' }}
                        />
                      </div>

                      {/* Budget details */}
                      <div className="row g-2" style={{ fontSize: '0.8rem' }}>
                        <div className="col-4">
                          <div className="text-muted mb-1">Cotizado</div>
                          <div className="fw-semibold">{formatCurrency(project.quotedAmount)}</div>
                        </div>
                        <div className="col-4">
                          <div className="text-muted mb-1">Gastado</div>
                          <div className={`fw-semibold text-${variant}`}>
                            {formatCurrency(project.totalExpenses)}
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="text-muted mb-1">Restante</div>
                          <div className={`fw-semibold ${remaining == null ? 'text-muted' : remaining < 0 ? 'text-danger' : 'text-success'}`}>
                            {formatCurrency(remaining)}
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                );
              })}
            </div>
          </Card.Body>
        </>
      )}

      <Card.Footer className="bg-light text-muted" style={{ fontSize: '0.85rem', padding: '0.875rem 1.25rem' }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <span>
            <i className="bi bi-info-circle me-1"></i>
            Click en una fila para ver detalles
          </span>
          <span>
            <i className="bi bi-arrow-clockwise me-1"></i>
            Auto-actualización cada 5 min
          </span>
        </div>
      </Card.Footer>
    </Card>
  );
};

BudgetOverviewWidget.propTypes = {
  autoRefreshInterval: PropTypes.number,
  limit: PropTypes.number,
  className: PropTypes.string,
};

export default BudgetOverviewWidget;
